// @greenlight-spec: 11-observability.md#cost-management
// @greenlight-spec: 16-api-conventions.md
import { z } from 'zod';
import { FleetRatesSchema } from '../types/fleet-billing.js';
const isoDateTime = () => z.string().datetime({ offset: true });
const isoDate = () => z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'expected YYYY-MM-DD');
const Usd = z.number().finite().nonnegative();
const Count = z.number().finite().nonnegative();
/** `list` is the published rates; any other source is the install's own card from the fleet. */
export const CostRatesSourceSchema = z.enum(['list', ...FleetRatesSchema.shape.source.options]);
/** Why the figures are at list rates when the install's own card could not be read. */
export const CostRatesFallbackSchema = z.enum([
    'not_enrolled',
    'unreachable',
    'refused',
    'malformed',
    'no_rates',
]);
/** The card that priced every figure. A `null` rate is a dimension the terms do not charge. */
export const CostRatesSchema = z.object({
    source: CostRatesSourceSchema,
    /** When the card took effect; the read time for list rates. */
    as_of: isoDateTime(),
    fallback: CostRatesFallbackSchema.optional(),
    usd_per_app_hour: Usd.nullable(),
    usd_per_deployment: Usd.nullable(),
    usd_per_mau_per_app: Usd.nullable(),
    usd_per_live_app: Usd.nullable(),
    monthly_fee_usd: Usd,
    monthly_license_usd: Usd.nullable(),
});
/** Billable quantity (app-hours, deployments, distinct users, or live apps) and its charge. */
export const CostDimensionSchema = z.object({ usage: Count, usd: Usd });
export const CostDimensionsSchema = z.object({
    app_hours: CostDimensionSchema,
    deployments: CostDimensionSchema,
    /** Distinct users over the trailing 28 days: what the month bills if it closed today. */
    mau_per_app: CostDimensionSchema,
    live_apps: CostDimensionSchema,
});
/** Lifetime carries the per-app dimensions only; live-app charges have no history. */
export const CostLifetimeDimensionsSchema = CostDimensionsSchema.omit({ live_apps: true });
export const CostLifetimeSchema = z.object({
    usd: Usd,
    /** First day of the earliest fully measured month. */
    measured_since: isoDate(),
    /** Some month in range was only partly measured, so the figure undercounts. */
    incomplete: z.boolean(),
});
export const CostTrendPointSchema = z.object({ day: isoDate(), usd: Usd });
export const TokenClassCountsSchema = z.object({
    input: Count,
    output: Count,
    cache_write_5m: Count,
    cache_write_1h: Count,
    cache_read: Count,
});
export const TokenCostWindowSchema = z.object({
    tokens: TokenClassCountsSchema,
    /** API-equivalent USD of the priced models; never money spent. */
    usd: Usd,
    /** Tokens of models with no price, left out of `usd`. */
    unpriced_tokens: Count,
    sessions: Count,
    models: z.array(z.object({
        model: z.string(),
        variant: z.string(),
        tokens: TokenClassCountsSchema,
        usd: Usd.nullable(),
    })),
});
export const TokenCostWindowsSchema = z.object({
    month_to_date: TokenCostWindowSchema,
    lifetime: TokenCostWindowSchema,
});
export const AppCostSchema = z.object({
    app_id: z.string(),
    /** Kept after the app is deleted. */
    slug: z.string(),
    /** `null` once the app is deleted. */
    name: z.string().nullable(),
    deleted: z.boolean(),
    month_to_date: CostDimensionsSchema,
    month_to_date_usd: Usd,
    /** `null` on the month's first day. */
    projected_month_usd: Usd.nullable(),
    /** `null` when the app has no lifetime usage (only a live-app charge or tokens). */
    lifetime: CostLifetimeSchema.extend({ dimensions: CostLifetimeDimensionsSchema }).nullable(),
    /** Counter cost per UTC day, oldest first; empty for an app with no usage this month. */
    trend: z.array(CostTrendPointSchema),
    /** Runtime and no requests over the idle lookback. */
    idle: z.boolean(),
    /** Build-session tokens; `null` when no session reported for this app. Never in the totals. */
    tokens: TokenCostWindowsSchema.nullable(),
});
export const InstallChargeSchema = z.object({
    kind: z.enum(['monthly_fee', 'license']),
    /** The whole charge: a monthly charge is never prorated, so this equals `month_usd`. */
    month_to_date_usd: Usd,
    /** The whole month, as the projection carries it. */
    month_usd: Usd,
});
/** `GET /api/cost` — what Greenlight charges this org (docs/11 §Cost Management). */
export const CostSummarySchema = z.object({
    as_of: isoDateTime(),
    rates: CostRatesSchema,
    total: z.object({
        month_to_date: CostDimensionsSchema,
        /** The apps' month-to-date plus every install charge. */
        month_to_date_usd: Usd,
        projected_month_usd: Usd.nullable(),
        lifetime: CostLifetimeSchema,
    }),
    install_charges: z.array(InstallChargeSchema),
    /** Apps with usage this month, idle, charged as live, with tokens, or with lifetime usage; highest month-to-date first. */
    apps: z.array(AppCostSchema),
    tokens: TokenCostWindowsSchema.extend({
        /** Whether the plugin currently reports build-session tokens for this org. */
        reporting_enabled: z.boolean(),
        /** When an org admin first turned reporting on; nothing before it is counted. */
        reporting_since: isoDateTime().nullable(),
    }),
});
//# sourceMappingURL=cost.js.map